import { Zone } from "@fairfleet/geotab";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import uniq from "lodash/uniq";
import { useAddInState } from "@/components/AddInStateProvider";
import { useGeotabClient } from "@/components/GeotabClientContext";
import createGroupFilter from "@/utilities/createGroupFilter";

/**
 * Hook to fetch zones by their IDs, using caching to minimize API calls.
 *
 * @param groups - The groups parameter used to filter zones.
 * @param groupFilterCondition - The condition applied to filter the groups.
 * @param [ids] - The list of zone IDs to fetch.
 * @returns - The list of zones, combining cached and newly fetched data.
 */
export function useZonesByIds(ids?: string[]) {
  const queryClient = useQueryClient();

  const { geotabClient, activeSessionId } = useGeotabClient();
  const { groups, groupFilterCondition } = useAddInState();
  // Remove duplicate IDs to avoid redundant fetches
  ids = ids ? uniq(ids) : ids;

  return useQuery({
    queryKey: ["zones", activeSessionId, ids, groups, groupFilterCondition],

    queryFn: async ({ signal }) => {
      // If there are no IDs, resolve immediately with an empty array
      if (!ids || ids.length === 0) {
        return Promise.resolve({});
      }

      // Arrays to store cached zones and IDs that need to be fetched
      const cachedZones: { [key: string]: Zone } = {};
      const idsToFetch: string[] = [];

      // Check each ID to see if it is already in the cache
      ids.forEach(id => {
        const cachedZone: Zone | undefined = queryClient.getQueryData([
          "zone",
          activeSessionId,
          id,
        ]);
        if (cachedZone) {
          cachedZones[cachedZone.id] = cachedZone;
        } else {
          idsToFetch.push(id);
        }
      });

      if (idsToFetch.length === 0) {
        return cachedZones;
      }

      // Fetch zones for IDs that were not found in the cache
      const fetchedZones = await Promise.all(
        idsToFetch.map(id =>
          geotabClient.get(
            "Zone",
            createGroupFilter(groups, groupFilterCondition, { id }),
            1,
            signal,
          ),
        ),
      );

      // Cache the newly fetched zones for future use
      fetchedZones.forEach(zone => {
        queryClient.setQueryData(["zone", activeSessionId, zone[0].id], zone[0]);
        cachedZones[zone[0].id] = zone[0];
      });
      return cachedZones;
    },

    enabled: !!ids && ids.length > 0,
  });
}
